import React from 'react';
import { useAppContext } from '../../../../Services/AppContext';
import PatientProfileEditCzech from './Czech/PatientProfileEditCzech';
import PatientProfileEditPeru from './Peru/PatientProfileEditPeru';
import PatientProfileEditEcuador from './Ecuador/PatientProfileEditEcuador';
import PatientProfileEditMexico from './Mexico/PatientProfileEditMexico';
import PatientProfileEditColombia from './Colombia/PatientProfileEditColombia';
import PatientProfileEditSlovakia from './Slovakia/PatientProfileEditSlovakia';
import apiClient from '../../../../Services/ApiClient';
import { useLazyWorkplaceMemberQuery, User } from '../../../../graphql/graphql';
import useNotification from '../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../Hooks/useTranslation';
import PatientProfileEditCzechChildren from './Czech/PatientProfileEditCzechChildren';
import PatientProfileEditHungary from './Hungary/PatientProfileEditHungary';
import PatientProfileEditPoland from './Poland/PatientProfileEditPoland';
import useApiClient from '../../../Base/Hooks/useApiClient';

interface Props {
	user: User;
	onSuccess: () => void;
	childId?: string;
}

const PatientProfileEdit: React.FC<Props> = ({ user, onSuccess, childId }) => {
	const [getWorkplaceMember] = useLazyWorkplaceMemberQuery();
	const { showNotificationSuccess, showNotificationAlert } = useNotification();
	const { t } = useTranslation();
	const { globalTemplate, workplaceId } = useAppContext();
	const { displayError } = useApiClient();

	const getWorkplaceMemberId = async (workplaceId: string) => {
		const { workplaceMember } = await getWorkplaceMember({
			userId: childId ? childId : user.id,
			workplaceId: workplaceId,
		}).unwrap();

		return workplaceMember?.id;
	};

	const handleSubmit = async (values: any) => {
		try {
			const workplaceMemberId = await getWorkplaceMemberId(workplaceId);
			if (workplaceMemberId) {
				const data: {
					legalRepresentative?: object;
					patient?: object;
				} = {};

				if (childId) {
					data.legalRepresentative = { id: user.id, ...values };
				} else {
					data.patient = values;
				}

				const result = await apiClient.patch(
					`/workplaces/${workplaceId}/patient/${workplaceMemberId}`,
					data,
				);

				if (Number(result.status) < 300) {
					showNotificationSuccess({
						content: t('base.shared.userEdited'),
					});

					onSuccess();
				}
			}
		} catch (e) {
			displayError(e);
		}
	};

	switch (globalTemplate) {
		case 'peru':
			return <PatientProfileEditPeru user={user} onSubmit={handleSubmit} />;
		case 'ecuador':
			return <PatientProfileEditEcuador user={user} onSubmit={handleSubmit} />;
		case 'mexico':
			return <PatientProfileEditMexico user={user} onSubmit={handleSubmit} />;
		case 'colombia':
			return <PatientProfileEditColombia user={user} onSubmit={handleSubmit} />;
		case 'slovakia':
			return <PatientProfileEditSlovakia user={user} onSubmit={handleSubmit} />;
		case 'hungary':
			return <PatientProfileEditHungary user={user} onSubmit={handleSubmit} />;
		case 'poland':
			return <PatientProfileEditPoland user={user} onSubmit={handleSubmit} />;
		default:
		case 'czechia':
			if (user.isChild) {
				return (
					<PatientProfileEditCzechChildren
						onSubmit={handleSubmit}
						user={user}
						workplaceId={workplaceId}
					/>
				);
			}
			return (
				<PatientProfileEditCzech
					user={user}
					onSubmit={handleSubmit}
					workplaceId={workplaceId}
				/>
			);
	}
};

export default PatientProfileEdit;
